import * as Sentry from '@sentry/vue'
import { getAuth, getIdToken, type User } from 'firebase/auth'
import cheerio from 'cheerio'
import type { CookieRef } from 'nuxt/app'
import { useStore } from '~/stores/main'
import { oneSignalService } from '~/composables/oneSignal'

import type { BannerCampaign, BasketLine, BasketLineAttribute, MiioProduct, ProductAttributes, ProductVariants, StoreBasket, VariantPriceContent } from '~/types/products'
import type { NifValidInfo, PaymentMethod, UserWallet, Vat, Vehicle, VehicleInfo, WalletPayment } from '~/types/user'
import { AddressType, AppStores, OrderStatus, ProductFeaturesCode, ShippingStatus, WalletType } from '~/types/enums'
import type { Promotion, StoreContent } from '~/types/global'
import { VehiclePlug, VehicleAdapter } from '~/types/vehicle'
import { UserCookie, type ShippingAddress } from '~/types/auth'
import type { FilteredRewards } from '~/types/orders'

export function throwError(error: unknown): void {
  const config = useRuntimeConfig()
  setDefaultErrorAlert()
  if (!config.public.isDeployed) {
    // eslint-disable-next-line no-console
    console.log(error)
  } else {
    Sentry.captureException(error instanceof Error ? error : new Error(JSON.stringify(error)))
  }
}

export function parseHtml(htmlString: string): string {
  const $ = cheerio.load(htmlString)
  $('[style]').removeAttr('style')
  $('h3').each((i, el) => {
    $(el).replaceWith(`<h4 class="text-dark font-semibold text-lg">${$(el).html()}</h4>`)
  })
  $('h2').each((i, el) => {
    $(el).replaceWith(`<h3 class="text-dark font-semibold text-xl">${$(el).html()}</h3>`)
  })
  return $.html()
}

export function trackEvent(content: object): void {
  try {
    const app = useNuxtApp()
    const gtm = useGtm()
    const store = useStore()

    let userData = null

    if (store.firebaseUser && !store.firebaseUser.isAnonymous) {
      let nameParts = store.firebaseUser.displayName?.split(' ')
      nameParts = nameParts ?? store.miioUser?.name?.split(' ')

      const addressNameParts = store.miioUser?.default_shipping_address?.name.split(' ')
      const shippingAddress = store.miioUser?.default_shipping_address?.shipping_address
      const city = store.miioUser?.default_shipping_address?.location
      const zip = store.miioUser?.default_shipping_address?.postal_code
      const country = store.miioUser?.default_shipping_address?.country?.toUpperCase() ?? app.$i18n.locale.value.toUpperCase()

      const firstName = addressNameParts ? addressNameParts[0] : nameParts ? nameParts[0] : null
      const lastName = addressNameParts && addressNameParts.length > 1 ? addressNameParts.slice(1).join(' ') : nameParts && nameParts.length > 1 ? nameParts.slice(1).join(' ') : null
      userData = {
        email_address: store.firebaseUser.email,
        phone_number: store.firebaseUser?.phoneNumber,
        address: {
          first_name: firstName,
          last_name: lastName,
          street: shippingAddress,
          city,
          postal_code: zip,
          country
        }
      }
    }

    const obj = {
      ...content,
      user_uid: store.firebaseUser?.uid,
      user_id: store.firebaseUser?.uid,
      language: app.$i18n.locale.value.toLowerCase(),
      user_data: userData
    }

    gtm?.trackEvent(obj)
    const eventName = obj.event
    delete obj.event
    app.$fb.query('trackCustom', eventName, obj)
  } catch (e) {
    Sentry.captureException(e)
  }
}

export async function updateUserLanguage(locale: string): Promise<void> {
  try {
    const store = useStore()
    const app = useNuxtApp()
    const userCookie = useCookie('hasUser')
    app.$i18n.setLocale(locale)
    if (userCookie.value && store.miioUser?.language !== locale) {
      const auth = getAuth()
      auth.languageCode = locale
      const { data } = await requestData(ApiIdentifier.SERVICES_API, `v2/user`, 'PATCH', { language: locale })
      if (userCookie.value === UserCookie.MiioUser) {
        store.miioUser = data
      }
    }
  } catch (e) {
    throwError(e)
  }
}

export function getVariantMaxQuantity(variant: ProductVariants, checkBasketQuantity?: boolean): number {
  const store = useStore()
  const line = store.basket?.lines.find((line: BasketLine): boolean => line.variant_id === variant.id)
  if (checkBasketQuantity && line) {
    return variant.stock.available - line.quantity
  } else {
    return variant.stock.available
  }
}

export function formatPrice(price: string): string {
  return price || '--'
}

export function getProductImage(product: MiioProduct): string {
  return product.images[0]?.original || `/img/empty-image-small.png`
}

export async function addProductToFavourites(product: MiioProduct, isFavourite: boolean): Promise<boolean> {
  const store = useStore()
  const app = useNuxtApp()
  try {
    if (!store.firebaseUser) {
      await createAnonymousUser()
    }
    if (isFavourite) {
      await requestData(ApiIdentifier.STORE_API, `favorites/`, 'POST', { product: product.id })
      const categories = returnTrackingCategories(product.categories)
      trackEvent({
        event: 'add_to_wishlist',
        items: [
          {
            item_id: product.id,
            item_name: product.title,
            ...categories
          }
        ]
      })
    } else {
      await requestData(ApiIdentifier.STORE_API, `favorites/${product.id}`, 'DELETE')
    }
    return true
  } catch (e) {
    throwError(e)
    store.error = { title: app.$i18n.t('addToFavouritesTitle'), description: app.$i18n.t('tryAgainLater') }
    return false
  }
}

export function addRecentlySeenProduct(product: MiioProduct): void {
  const store = useStore()
  const recentlySeenProducts = store.recentProducts
  const alreadySeen = recentlySeenProducts.find((value: MiioProduct) => value.id === product.id)
  if (alreadySeen) {
    const index = recentlySeenProducts.indexOf(alreadySeen)
    if (index !== 0) {
      recentlySeenProducts.splice(index, 1)
      recentlySeenProducts.unshift(alreadySeen)
    }
  } else {
    store.recentProducts = [product, ...recentlySeenProducts.slice(0, 10)]
  }
}

export async function getDefaultVehicle(): Promise<void> {
  const store = useStore()
  const { data } = await requestData(ApiIdentifier.SERVICES_API, `v2/user/cars`)
  if (data && data.length > 0) {
    const defaultVehicle = data.find((vehicle: VehicleInfo) => vehicle.default)
    if (defaultVehicle) {
      store.defaultVehicle = defaultVehicle
    }
  }
}

export async function addNewVehicle(payload: object): Promise<void> {
  const store = useStore()
  const { data } = await requestData(ApiIdentifier.SERVICES_API, `v2/user/cars`, 'POST', payload)
  if (!store.defaultVehicle && data?.id) {
    await setDefaultVehicle(data.id)
  }
  store.defaultVehicle = data
}

export async function setDefaultVehicle(id: number): Promise<void> {
  const store = useStore()
  const { data } = await requestData(ApiIdentifier.SERVICES_API, `v2/user/cars/${id}`, 'PATCH', { default: true })
  store.defaultVehicle = data
}

export function verifyCompatibleVehicle(product: MiioProduct): boolean {
  const store = useStore()
  const defaultVehicle = store.defaultVehicle
  if (defaultVehicle) {
    const vehiclePlug = defaultVehicle.vehicle_version?.ac_charge_port
    const productPlug = product.attributes?.find((v) => v.code === 'plug')?.value || null
    return vehiclePlug != null && productPlug !== null && productPlug === vehiclePlug
  }
  return false
}
export function calcChargingDuration(product: MiioProduct): number | null {
  const store = useStore()
  const defaultVehicle = store.defaultVehicle
  const compatible = verifyCompatibleVehicle(product)
  if (compatible && defaultVehicle) {
    const batteryCapacity = 0.6 * defaultVehicle?.vehicle_version?.battery_useable
    const maxPower = getChargingPower(product, defaultVehicle.vehicle)
    if (maxPower === 0) return null
    return Math.round(batteryCapacity / maxPower)
  }
  return null
}
export function getChargingPower(product: MiioProduct, vehicle: Vehicle): number {
  const chargerPowerAttribute = product.attributes?.find((attr) => attr.code === 'max_power')
  const chargerPower = chargerPowerAttribute ? parseFloat(chargerPowerAttribute.value) : 0
  const vehicleACPower = vehicle?.charging[0]?.ac_max_power || 0
  return Math.min(chargerPower, vehicleACPower)
}

export function getChargingTime(batteryCapacity: number, power: number) {
  return Math.round((batteryCapacity * 0.6) / power) || 0
}

export async function refreshUserToken(): Promise<void> {
  const auth = getAuth()
  if (!auth.currentUser) return
  const authToken: CookieRef<string | null> = useCookie('authToken')
  authToken.value = await getIdToken(auth.currentUser)
}

export function setDefaultErrorAlert(): void {
  const store = useStore()
  const app = useNuxtApp()
  store.error = { title: app.$i18n.t('somethingWentWrong'), description: app.$i18n.t('tryAgainLater') }
}

export async function getUserDetails(): Promise<void> {
  const store = useStore()
  const { data } = await requestData(ApiIdentifier.SERVICES_API, `v2/users`)
  store.miioUser = data
  if (data?.user_status?.miio_pay_finished) {
    await oneSignalService.addTag('has_seen_the_miiopay_tutorial', data.user_status.miio_pay_finished ? '1' : '0')
  }
  const { data: wallets } = await requestData(ApiIdentifier.SERVICES_API, `v3/user/wallet`)
  if (wallets) store.wallets = wallets?.filter((wallet) => wallet.is_owner)
  await getRewards()
}

export async function getRewards() {
  const store = useStore()
  const { data } = await requestData(ApiIdentifier.SERVICES_API, `/v3/wallet/wallet-balance-rewards-list/?wallet_id=`)
  if (data) store.rewards = data?.results?.length > 0 ? data.results : null
}

export async function changeUserDetails(obj: object): Promise<void> {
  await requestData(ApiIdentifier.SERVICES_API, `/v2/users`, 'PATCH', obj)
  await getUserDetails()
}

export async function finishOnboarding(): Promise<void> {
  changeRoute('index')
  await changeUserDetails({ user_on_boarding_store: true })
}

export function filterProductAttributes(attributes: BasketLineAttribute[]): BasketLineAttribute[] {
  return attributes.filter((attribute) => attribute.value !== null && attribute.value !== '')
}

export async function getBasketContent(): Promise<void> {
  const store = useStore()
  if (!store.firebaseUser) {
    await createAnonymousUser()
  }
  const { data, error } = await requestData(ApiIdentifier.STORE_API, `basket/`)
  if (error) {
    store.error = error
  } else if (data) {
    formatBasketData(data)
  }
}

export async function mergeAnonymousBasket(uid: string): Promise<void> {
  await refreshUserToken()
  const { data, error } = await requestData(ApiIdentifier.STORE_API, `merge-anonymous-data/`, 'POST', { anonymous_uid: uid })
  if (data && (data.basket_lines_merged || data.authenticated_user_coupon)) {
    useState('hasMerge', (): boolean => true)
    changeRoute('basket')
  }
  if (error) {
    changeRoute('basket')
  }
}

export async function getChargingPreferences(): Promise<void> {
  const store = useStore()
  const { data } = await requestData(ApiIdentifier.SERVICES_API, `v2/user/charging-preferences`)
  store.chargingPreferences = data && data.length > 0 ? data[0] : null
}

export async function createOrUpdateChargingPreferences(payload: object): Promise<void> {
  const store = useStore()
  const { data } = await requestData(
    ApiIdentifier.SERVICES_API,
    `v2/user/charging-preferences${store.chargingPreferences ? '/' + store.chargingPreferences.id : ''}`,
    store.chargingPreferences ? 'PATCH' : 'POST',
    payload
  )
  store.chargingPreferences = data
}

export function formatBasketData(data: StoreBasket): void {
  const store = useStore()
  data.lines.forEach((line: BasketLine) => {
    line.deleteByUser = false
  })
  store.basket = data
}

function getWalletName(wallet: UserWallet): string {
  const app = useNuxtApp()
  if (wallet.type === WalletType.Business) {
    return wallet.vat?.name || app.$i18n.t('businessWallet')
  } else if (wallet.type === WalletType.Personal) {
    return app.$i18n.t('personalWallet')
  }
}

export async function getPaymentMethods() {
  const store = useStore()
  let wallets: WalletPayment[] = []
  try {
    const [walletResponse, paymentMethodsResponse] = await Promise.all([
      requestData(ApiIdentifier.SERVICES_API, `v3/user/wallet`),
      requestData(ApiIdentifier.SERVICES_API, `v2/gateway/user/payment-methods`)
    ])
    const wallet = walletResponse.data
    wallets = wallet
      .filter((item: UserWallet) => item.is_owner)
      .map((item: UserWallet) => ({
        walletId: item.id,
        name: getWalletName(item),
        balance: item.balance,
        id: item.wallet_pay,
        type: 'wallet_pay',
        wallet_type: item.type,
        blocked: item.block_reason.length > 0,
        virtual_balance_without_bonus: item.virtual_balance_without_bonus
      }))
    const paymentMethods = paymentMethodsResponse.data
    store.paymentMethods = [
      ...(paymentMethods.length > 0
        ? paymentMethods.filter((item: PaymentMethod) => {
            return item.type === 'mb' || (['cc', 'mbw'].includes(item.type) && item.state === 'ACCEPTED')
          })
        : []),
      ...wallets
    ]
  } catch (error) {
    store.error = error
  }
}

export async function getUserVat(): Promise<void> {
  const store = useStore()
  const { data, error } = await requestData(ApiIdentifier.SERVICES_API, `v2/user/nifs?final_consumer=true&valid=true`)
  if (data) {
    store.vats = data.length > 0 ? data : null
  } else if (error) {
    store.error = error
  }
}

export function checkInvalidNif(validInfo: NifValidInfo | null): boolean {
  if (validInfo && validInfo.errors && validInfo.errors.length > 0) {
    const error = validInfo.errors[0]
    if (error === 'Contribuinte não é válido' || error === 'Fiscal não é válido') {
      return true
    }
  }
  return false
}

export function checkInvalidVatEmail(validInfo: NifValidInfo | null): boolean {
  if (validInfo && validInfo.errors && validInfo.errors.length > 0) {
    const error = validInfo.errors[0]
    if (error === 'Client contact is invalid') {
      return true
    }
  }
  return false
}

export async function getProductCategories(): Promise<void> {
  const store = useStore()
  const { data } = await requestData(ApiIdentifier.STORE_API, `categories/`)
  if (data && data.results.length > 0) {
    store.categories = data.results
  }
}

export async function getShippingAddressList(): Promise<void> {
  const store = useStore()
  const { data, error } = await requestData(ApiIdentifier.SERVICES_API, `v2/user/shipping-address`)
  if (data) {
    const defaultAddressId = store.miioUser?.default_shipping_address?.id
    if (defaultAddressId) {
      const defaultAddressIndex = data.findIndex((obj) => obj.id === defaultAddressId)
      if (defaultAddressIndex !== -1) {
        const defaultAddressObj = data.splice(defaultAddressIndex, 1)[0]
        data.unshift(defaultAddressObj)
      }
    }
    store.shippingAddressList = data.length > 0 ? data : null
  } else if (error) {
    store.error = error
  }
}

export async function updateUserDefaultAddress(obj: { default_shipping_address?: number; default_invoice_info?: number }): Promise<void> {
  const store = useStore()
  const { error } = await requestData(ApiIdentifier.SERVICES_API, `v2/users`, 'PATCH', obj)
  if (error) {
    store.error = error
  } else {
    await getUserDetails()
  }
}

export async function removeVariantFromBasket(variant: BasketLine, quantity: number): Promise<void> {
  const store = useStore()
  const app = useNuxtApp()

  const { error } = await requestData(ApiIdentifier.STORE_API, `basket/remove/${variant.variant_id}/`, 'DELETE', { quantity })

  if (error) {
    store.error = { title: app.$i18n.t('somethingWentWrong'), description: app.$i18n.t('changeProductQuantityError') }
  } else {
    const categories = returnTrackingCategories(variant.categories)
    const items = [
      {
        item_id: variant.parent_id,
        item_name: variant.title,
        discount: calcDiscount(variant.line_price),
        item_variant: variant.variant_id,
        price: variant.line_price.incl_tax_incl_discounts,
        quantity,
        ...categories
      }
    ]
    trackEvent({
      event: 'remove_from_cart',
      currency: variant.line_price.currency,
      value: variant.line_price.incl_tax_incl_discounts,
      items
    })
  }
}

export async function removeOutOfStockProducts(products: number[]) {
  const store = useStore()
  for (const productVariant of products) {
    const variant = await getVariantInfoFromBasket(productVariant)
    if (variant) {
      await removeVariantFromBasket(variant, variant.quantity)
    }
  }
  await getBasketContent()
  store.error = null
}

export function openMiioAppStores(option: AppStores) {
  const app = useNuxtApp()
  const locale = app.$i18n.locale.value
  let ios = 'https://apps.apple.com/pt/app/miio/id1462182013'
  const android = 'https://bit.ly/miioandroid'
  let web = 'https://app.miio.pt/'
  if (locale === 'fr') {
    web = 'https://app.miio.fr/'
    ios = 'https://apps.apple.com/fr/app/miio/id1462182013'
  } else if (locale === 'en') {
    web = 'https://app.miioelectric.com/'
    ios = 'https://apps.apple.com/app/miio/id1462182013'
  } else if (locale === 'es') {
    ios = 'https://apps.apple.com/es/app/miio/id1462182013'
  }

  const appStoreUrls = {
    [AppStores.AppStore]: ios,
    [AppStores.PlayStore]: android,
    [AppStores.WebApp]: web
  }

  if (option in appStoreUrls) {
    window.open(appStoreUrls[option], '_blank')
  }
}

export function formatDate(inputDate: string): string {
  const date = new Date(inputDate)
  const day = date.getUTCDate().toString().padStart(2, '0')
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
  const year = date.getUTCFullYear()
  return `${day}/${month}/${year}`
}

export function getOrderStatus(status: string): string {
  const app = useNuxtApp()
  switch (status) {
    case OrderStatus.Pending:
      return app.$i18n.t('confirmingPayment')
    case OrderStatus.BeingProcessed:
      return app.$i18n.t('inPreparation')
    case OrderStatus.PartiallyShipped:
    case OrderStatus.Shipped:
      return app.$i18n.t('inTransit')
    case OrderStatus.Delivered:
      return app.$i18n.t('delivered')
    case OrderStatus.FailedPayment:
      return app.$i18n.t('failedPayment')
    case OrderStatus.CanceledByUser:
    case OrderStatus.CanceledByFailedPayment:
      return app.$i18n.t('cancelled')
    case OrderStatus.Refunded:
      return app.$i18n.t('refunded')
    default:
      return app.$i18n.t('unknownState')
  }
}

export function getShippingStatus(status: string): string {
  const app = useNuxtApp()
  switch (status) {
    case ShippingStatus.Pending:
      return app.$i18n.t('confirmingPayment')
    case ShippingStatus.BeingProcessed:
      return app.$i18n.t('inPreparation')
    case ShippingStatus.Shipped:
      return app.$i18n.t('inTransit')
    case ShippingStatus.Delivered:
      return app.$i18n.t('delivered')
    case ShippingStatus.Returned:
      return app.$i18n.t('returned')
    case ShippingStatus.Canceled:
      return app.$i18n.t('cancelled')
    default:
      return app.$i18n.t('unknownState')
  }
}

export async function handleStoreError(error): Promise<void> {
  const store = useStore()
  if (error.data) {
    store.error = error.data
    const code = error.data.code
    if (code === '01-0004') {
      await removeBasketVoucher()
    }
  } else {
    store.error = error
  }
}

export async function removeBasketVoucher(): Promise<void> {
  const store = useStore()
  const id = store.basket?.voucher_discounts[0]?.id
  const { data, error } = await requestData(ApiIdentifier.STORE_API, `basket/remove-voucher/${id}/`, 'DELETE')
  if (data) {
    await getBasketContent()
  } else if (error) {
    await handleStoreError(error)
  }
}

export function getVariantInfoFromBasket(variantID: number) {
  const store = useStore()
  if (!store.basket || !store.basket.lines.length) return null
  return store.basket.lines.find((product) => product.variant_id === variantID) || null
}

export function getProductTitle(variantID: number): string | null {
  const variant = getVariantInfoFromBasket(variantID)
  return variant ? variant.title : null
}

export function kvaToKw(kva: number): number {
  const kw = kva * 0.9
  return parseFloat(kw.toFixed(2))
}

export function getKvaOptions(): number[] {
  return [1.15, 2.3, 3.45, 4.6, 5.75, 6.9, 10.35, 13.8, 17.25, 20.7, 27.5, 34.5, 41.4]
}

export function getPlacesOptions(): { name: string; type: string }[] {
  const app = useNuxtApp()
  return [
    { name: app.$i18n.t('house'), type: 'house' },
    { name: app.$i18n.t('flat'), type: 'flat' },
    { name: app.$i18n.t('company'), type: 'company' },
    { name: app.$i18n.t('other'), type: 'other' }
  ]
}

export function getChargingPhases(): { name: string; value: number }[] {
  const app = useNuxtApp()
  return [
    { name: app.$i18n.t('monoPhase'), value: 1 },
    { name: app.$i18n.t('triPhase'), value: 3 }
  ]
}

export function getVehicleTypes() {
  const app = useNuxtApp()
  return [
    { name: app.$i18n.t('electricCar'), shortName: app.$i18n.t('electric'), value: 'ELECTRIC_CAR' },
    { name: app.$i18n.t('plugInHybrid'), shortName: app.$i18n.t('plugin'), value: 'PLUGIN_CAR' },
    { name: app.$i18n.t('electricMotorcycle'), shortName: app.$i18n.t('motorcycle'), value: 'ELECTRIC_MOTORCYCLE' }
  ]
}

export async function getBrands(type: string) {
  const { data } = await requestData(ApiIdentifier.SERVICES_API, `v2/vehicles-brands/${type}`)
  if (data) {
    return data.brands
  }
  return null
}

export async function getModels(brand: string, type: string) {
  const { data } = await requestData(ApiIdentifier.SERVICES_API, `v2/vehicles?search=${brand},${type}&limit=100`)
  if (data && data.results.length > 0) {
    return data.results
  }
  return null
}

export async function checkVatNumber(nif: string, data: Vat): Promise<boolean> {
  const shouldUpdateVat: boolean = nif === '999999990' || !checkInvalidNif(data.valid_info)
  if (shouldUpdateVat) {
    await updateBasketVat(data.id)
    return true
  } else {
    return false
  }
}

export async function updateVatNif(id: number, nif: number): Promise<boolean> {
  const store: StoreContent = useStore()
  const { data, error } = await requestData(ApiIdentifier.SERVICES_API, `v2/user/nifs/${id}`, 'PATCH', { number: nif })
  if (data) {
    return await checkVatNumber(String(nif), data)
  } else if (error) {
    store.error = error
  }
  return false
}

export async function updateBasketVat(id: number): Promise<void> {
  const store: StoreContent = useStore()
  const { data, error } = await requestData(ApiIdentifier.STORE_API, `basket/billing_address/`, 'PATCH', { user_vat_id: id })
  if (data) {
    store.basket = data
    await getUserVat()
  } else if (error) {
    store.error = error
  }
}

export function filterActiveCampaigns(data: BannerCampaign[]) {
  const currentDate: Date = new Date()
  const campaigns: BannerCampaign[] = data.filter((banner: BannerCampaign) => {
    const beginDate: Date = new Date(banner.begin_date)
    const validUntil: Date = new Date(banner.valid_until)
    return currentDate >= beginDate && currentDate <= validUntil
  })
  if (campaigns.length === 0) return null
  else {
    const sort = campaigns.sort((a: { order: number }, b: { order: number }) => a.order - b.order)
    return sort.map((banner: BannerCampaign) => {
      return {
        mobile: banner.image_small ? banner.image_small.url : banner.image.url,
        tablet: banner.image_medium ? banner.image_medium.url : banner.image.url,
        desktop: banner.image.url,
        share_link: banner.share_link
      }
    })
  }
}

export function clearSearch(): void {
  const route = useRoute()
  const router = useRouter()
  const searchState = useState('search')
  searchState.value = null
  const { search, ...others } = route.query
  if (search) {
    router.replace({ query: others })
  }
}

export function returnTrackingCategories(categories: { id: number; name: string; code: string }[]): object {
  const categoryData = {}
  if (!categories || categories.length === 0) return categoryData
  categories.forEach((category, index) => {
    if (index === 0) {
      categoryData.item_category = category.code || null
    } else {
      categoryData[`item_category${index + 1}`] = category.code || null
    }
  })
  return categoryData
}

export function calcDiscount(productVariantPrice: VariantPriceContent): number {
  if (productVariantPrice.incl_tax_excl_discounts !== null && productVariantPrice.incl_tax_incl_discounts !== null) {
    return productVariantPrice.incl_tax_excl_discounts - productVariantPrice.incl_tax_incl_discounts
  }
  return 0
}

export async function addMbwayAuthorizedPayments(id: number): Promise<boolean> {
  const store = useStore()
  const app = useNuxtApp()
  const { error } = await requestData(ApiIdentifier.SERVICES_API, `v2/gateway/user/payment-methods/${id}/mandates`, 'POST')
  if (error) {
    store.error = { title: app.$i18n.t('errorOccurred'), description: app.$i18n.t('mbwayMandateError') }
    return false
  }
  return true
}

export function getOptionComplement(code: string) {
  if (code === ProductFeaturesCode.MaxPower) {
    return 'kW'
  } else if (code === ProductFeaturesCode.Cable || code === ProductFeaturesCode.Meters) {
    return 'm'
  }
  return ''
}

export function attributeNotCompatible(attribute: ProductAttributes, cable: boolean): boolean {
  const store = useStore()
  if (store.defaultVehicle) {
    if (attribute.code === ProductFeaturesCode.Plug) {
      const plug = attribute.value
      return !plugIsCompatible(plug, cable)
    } else if (attribute?.code === ProductFeaturesCode.MaxPower) {
      if (!store.defaultVehicle?.vehicle_version?.ac_max_power) return false
      const maxPower = parseFloat(attribute.value)
      const vehicleMaxPower = store.defaultVehicle?.vehicle_version?.ac_max_power || 0
      return maxPower < vehicleMaxPower
    }
  }
  return false
}
export function plugIsCompatible(plug: string, cable: boolean): boolean {
  const store = useStore()
  const compatiblePorts: VehiclePlug[] = getAllCompatiblePorts()
  if (plug === VehiclePlug.Type2 && cable !== null) {
    const adapters = store.defaultVehicle?.adapters || null
    if (adapters && adapters.includes(VehicleAdapter.Type1ToType2) && cable) {
      compatiblePorts.splice(compatiblePorts.indexOf(VehiclePlug.Type2), 1)
    }
  }
  return compatiblePorts.includes(plug)
}

function getAllCompatiblePorts(): VehiclePlug[] {
  const store = useStore()
  const isTesla = store.defaultVehicle?.vehicle?.brand?.name.toLowerCase() === 'tesla'
  const adapters = store.defaultVehicle?.adapters || null
  const vehiclePort = store.defaultVehicle?.vehicle_version?.ac_charge_port || null

  const ports: VehiclePlug[] = []
  if (vehiclePort) {
    ports.push(vehiclePort)
  }
  const adapterToPlugMap = {
    [VehicleAdapter.Type1ToType2]: VehiclePlug.Type2,
    [VehicleAdapter.TeslaCSS2]: VehiclePlug.CCS2,
    [VehicleAdapter.TeslaChademo]: VehiclePlug.Chademo,
    [VehicleAdapter.Schuko]: VehiclePlug.Schuko,
    [VehicleAdapter.SchukoToType2]: VehiclePlug.Type2
  }
  if (adapters !== null) {
    adapters.forEach((adapter) => {
      const plug = adapterToPlugMap[adapter]
      if (plug) {
        ports.push(plug)
      }
    })
  }
  if (isTesla) {
    ports.push(VehiclePlug.CCS2TeslaV3)
    if (ports.includes(VehiclePlug.CCS2) || ports.includes(VehiclePlug.Type2)) {
      ports.push(VehiclePlug.Type2Tesla)
    }
    if (ports.includes(VehiclePlug.Type2)) {
      ports.push(VehiclePlug.Type2TeslaSupercharger)
    }
  }
  return ports
}

export async function getActivePromos(): Promise<void> {
  const store = useStore()
  const { data } = await requestData(ApiIdentifier.STORE_API, `offer-groups/`)
  store.promos = data && data.results.length >= 0 ? data.results : null
}

export function getAllLanguageSlugs() {
  const { $i18n } = useNuxtApp()
  const allLanguageSlugs = $i18n.locales.value.map((locale: { code: string }) => `slug_${locale.code}`)
  allLanguageSlugs.push('slug')
  return allLanguageSlugs
}

export function getCompatibleCategory(categoryQuery: string) {
  const store = useStore()
  const slugs = getAllLanguageSlugs()
  return store.categories?.find((category) => {
    return slugs.some((slugKey: string) => category.seo[slugKey] === categoryQuery)
  })
}

export function getCompatiblePromo(promotion: string): Promise<Promotion | undefined> {
  const store = useStore()
  const slugs = getAllLanguageSlugs()
  if (!store.promos || store.promos?.length === 0) return null
  return store.promos?.find((promo) => {
    return slugs.some((slugKey: string) => promo.seo[slugKey] === promotion)
  })
}

export async function updateBasketAddress(type: AddressType, selectedAddress: ShippingAddress) {
  const store = useStore()
  const app = useNuxtApp()
  let body = {}
  if (type === AddressType.Shipping) {
    body = { shipping_address_id: selectedAddress?.id }
  } else if (type === AddressType.Billing) {
    body = { user_vat_id: selectedAddress?.id }
  }
  const option = type === AddressType.Shipping ? 'shipping_address' : 'billing_address'
  const { data, error } = await requestData(ApiIdentifier.STORE_API, `basket/${option}/`, 'PATCH', body)
  if (data) {
    store.basket = data
    const items = store.basket?.lines.map((line, index) => {
      const categoryData = returnTrackingCategories(line.categories)
      return {
        item_id: line.parent_id,
        item_name: line.title,
        coupon: store.basket?.voucher_discounts?.length > 0 ? store.basket.voucher_discounts[0].voucher.code : null,
        discount: calcDiscount(line.line_price),
        item_variant: line.variant_id,
        price: line.line_price.incl_tax_incl_discounts,
        quantity: line.quantity,
        index,
        ...categoryData
      }
    })
    trackEvent({
      event: 'add_shipping_info',
      coupon: store.basket?.voucher_discounts?.[0]?.voucher.code,
      currency: store.basket?.total_price?.currency,
      value: store.basket?.total_price?.incl_tax_incl_discounts,
      items
    })
  } else if (error) {
    if (type === AddressType.Shipping) {
      store.error = { title: app.$i18n.t('errorOccurred'), description: app.$i18n.t('addShippingError') }
    } else if (type === AddressType.Billing) {
      store.error = { title: app.$i18n.t('errorOccurred'), description: app.$i18n.t('addVatError') }
    }
  }
}

export function getTotalCashbackAmount(rewards: FilteredRewards[]) {
  return rewards.reduce((totalAmount, reward) => totalAmount + parseFloat(reward.amount), 0).toFixed(2) || 0
}

export function checkIfStringsOfListIncludes(list: string[], whatToBeIncluded: string): boolean {
  try {
    return list.some((item) => whatToBeIncluded.includes(item))
  } catch (e) {
    Sentry.captureException(e)
    return false
  }
}
